/**
 * Sparkline SVG simple para cards financieras del dashboard de Tesorería.
 *
 * Sigue el handoff: linea + area con gradiente + dot final con halo
 * + tendencia opcional. Sin librerías externas (60 lineas de SVG).
 */
import { useId } from 'react';

export interface SparklineProps {
  /** Valores cronológicos. Mínimo 2 para mostrar línea. */
  data: number[];
  /** Color base de la línea (CSS color). Default: --orange. */
  color?: string;
  /** Ancho en px del SVG. */
  width?: number;
  /** Alto en px del SVG. */
  height?: number;
  className?: string;
  /** ARIA label para accesibilidad. */
  ariaLabel?: string;
  /** Si true, el SVG ocupa el 100% del ancho del contenedor (banda C1). */
  fullWidth?: boolean;
}

export function Sparkline({
  data,
  color = '#ef6320',
  width = 120,
  height = 36,
  className,
  ariaLabel = 'Tendencia',
  fullWidth = false,
}: SparklineProps) {
  const gradId = useId();

  if (data.length < 2) {
    return (
      <div
        className={`text-[10px] text-text-muted italic ${className ?? ''}`}
        style={{
          width: fullWidth ? '100%' : width,
          height,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        sin tendencia aún
      </div>
    );
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const padding = 2;

  const points = data.map((v, i) => {
    const x = padding + (i / (data.length - 1)) * (width - padding * 2);
    const y = height - padding - ((v - min) / range) * (height - padding * 2);
    return [x, y] as const;
  });

  const linePath = points
    .map(([x, y], i) => (i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`))
    .join(' ');
  const areaPath =
    linePath +
    ` L ${points.at(-1)![0]} ${height - padding}` +
    ` L ${points[0]![0]} ${height - padding} Z`;

  const [lastX, lastY] = points.at(-1)!;

  return (
    <svg
      width={fullWidth ? '100%' : width}
      height={height}
      viewBox={fullWidth ? `0 0 ${width} ${height}` : undefined}
      preserveAspectRatio={fullWidth ? 'none' : undefined}
      role="img"
      aria-label={ariaLabel}
      className={className}
      style={fullWidth ? { display: 'block' } : undefined}
    >
      <defs>
        <linearGradient id={`spark-${gradId}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#spark-${gradId})`} />
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect={fullWidth ? 'non-scaling-stroke' : undefined}
      />
      <circle cx={lastX} cy={lastY} r="5" fill={color} opacity="0.18" />
      <circle cx={lastX} cy={lastY} r="2.4" fill={color} />
    </svg>
  );
}

export interface TrendPillProps {
  /** Variación % vs mes anterior. `null` = "sin dato suficiente". */
  pct: number | null;
  /** Cuando `true`, subir es bueno (verde) y bajar es malo (rojo).
   * Cuando `false`, semántica invertida (típico de gastos). */
  higherIsBetter?: boolean;
  /** Texto del tooltip aclaratorio (ej. "jun vs may, a igual fecha"). */
  title?: string;
}

export function TrendPill({
  pct,
  higherIsBetter = true,
  title = 'Variación vs mes anterior, a igual fecha',
}: TrendPillProps) {
  if (pct === null) {
    return (
      <span
        title={title}
        className="num text-[10px] text-text-muted px-1.5 py-0.5 rounded-full bg-white/[0.04]"
      >
        —
      </span>
    );
  }
  const isUp = pct > 0;
  const isDown = pct < 0;
  const positivo =
    (isUp && higherIsBetter) || (isDown && !higherIsBetter);
  const negativo =
    (isDown && higherIsBetter) || (isUp && !higherIsBetter);
  let color = 'text-text-muted';
  if (positivo) color = 'text-emerald-400';
  if (negativo) color = 'text-red-400';
  const arrow = isUp ? '↑' : isDown ? '↓' : '→';
  const display = Math.abs(pct).toFixed(1).replace('.', ',');
  return (
    <span
      title={title}
      className={`num text-[10px] font-medium px-1.5 py-0.5 rounded-full ${color}`}
      style={{
        backgroundColor: positivo
          ? 'rgba(95,217,177,0.12)'
          : negativo
            ? 'rgba(255,122,122,0.12)'
            : 'rgba(255,255,255,0.04)',
      }}
    >
      {arrow} {display} %
    </span>
  );
}
